import { FormEvent, useState } from 'react'
import { updateCourseAssignment, type CourseAssignment, type QuizQuestion } from '@/lib/assignments'

const MAX_QUESTIONS = 10
const OPTIONS_PER_QUESTION = 3

function emptyQuestion(): QuizQuestion {
  return { question: '', options: ['', '', ''], correctIndex: 0 }
}

function normalizeQuestions(list?: QuizQuestion[]) {
  if (!list || list.length === 0) return [emptyQuestion()]
  return list.map((item) => ({
    question: item.question || '',
    options: [item.options?.[0] || '', item.options?.[1] || '', item.options?.[2] || ''],
    correctIndex: item.correctIndex ?? 0,
  }))
}

interface QuizEditorProps {
  assignment: CourseAssignment
  onSaved: (record: CourseAssignment) => void
}

function QuizEditor({ assignment, onSaved }: QuizEditorProps) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState(assignment.title)
  const [description, setDescription] = useState(assignment.description || '')
  const [dueLabel, setDueLabel] = useState(assignment.dueLabel || '')
  const [externalUrl, setExternalUrl] = useState(assignment.externalUrl || '')
  const [questions, setQuestions] = useState<QuizQuestion[]>(() => normalizeQuestions(assignment.questions))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const addQuestion = () => {
    setQuestions((list) => (list.length >= MAX_QUESTIONS ? list : [...list, emptyQuestion()]))
  }

  const removeQuestion = (index: number) => {
    setQuestions((list) => list.filter((_, i) => i !== index))
  }

  const updateQuestionText = (index: number, value: string) => {
    setQuestions((list) => list.map((item, i) => (i === index ? { ...item, question: value } : item)))
  }

  const updateOptionText = (index: number, optionIndex: number, value: string) => {
    setQuestions((list) =>
      list.map((item, i) => {
        if (i !== index) return item
        const options = [...item.options]
        options[optionIndex] = value
        return { ...item, options }
      }),
    )
  }

  const updateCorrectIndex = (index: number, optionIndex: number) => {
    setQuestions((list) => list.map((item, i) => (i === index ? { ...item, correctIndex: optionIndex } : item)))
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!title.trim()) {
      setError('Escribe un título.')
      return
    }
    if (questions.length === 0) {
      setError('Añade al menos una pregunta al quiz.')
      return
    }
    for (const item of questions) {
      if (!item.question.trim()) {
        setError('Cada pregunta necesita un enunciado.')
        return
      }
      if (item.options.some((option) => !option.trim())) {
        setError('Completa las 3 opciones de cada pregunta.')
        return
      }
    }
    setError('')
    setMessage('')
    setBusy(true)
    try {
      const record = await updateCourseAssignment(assignment._id, {
        title,
        description,
        dueLabel,
        externalUrl,
        questions: questions.map((item) => ({
          question: item.question.trim(),
          options: item.options.map((option) => option.trim()),
          correctIndex: item.correctIndex,
        })),
      })
      onSaved(record)
      setMessage('Preguntas actualizadas.')
    } catch (err) {
      console.error('No se pudo actualizar el quiz:', err)
      setError('No se pudo guardar. Comprueba que tu cuenta es administradora.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-4 border-t border-[#1E3A5F]/10 pt-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-[0.18em] text-[#C41E3A]">
          Preguntas ({assignment.questions?.length || 0})
        </p>
        <button type="button" onClick={() => setOpen((value) => !value)} className="text-xs underline">
          {open ? 'Cerrar edición' : 'Editar preguntas'}
        </button>
      </div>

      {!open ? (
        assignment.questions && assignment.questions.length > 0 ? (
          <ol className="mt-3 space-y-3">
            {assignment.questions.map((item, index) => (
              <li key={`${assignment._id}-q-${index}`} className="border border-[#1E3A5F]/10 p-3">
                <p className="text-sm font-medium">
                  {index + 1}. {item.question}
                </p>
                <ul className="mt-2 space-y-1 text-sm text-[#1E3A5F]/70">
                  {(item.options || []).map((option, optionIndex) => (
                    <li key={`${assignment._id}-q-${index}-o-${optionIndex}`}>
                      {optionIndex === item.correctIndex ? '● ' : '○ '}
                      {option}
                      {optionIndex === item.correctIndex ? ' (correcta)' : ''}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        ) : (
          <p className="mt-2 text-xs text-[#1E3A5F]/50">Este quiz aún no tiene preguntas. Ábrelo para añadirlas.</p>
        )
      ) : (
        <form onSubmit={onSubmit} className="mt-3 space-y-3">
          <label className="block text-xs">
            Título
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-xs">
            Enunciado
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={2}
              className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs">
              Fecha límite (texto)
              <input
                value={dueLabel}
                onChange={(event) => setDueLabel(event.target.value)}
                className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-xs">
              Liga externa
              <input
                type="url"
                value={externalUrl}
                onChange={(event) => setExternalUrl(event.target.value)}
                className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
              />
            </label>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.18em] text-[#C41E3A]">
              Preguntas ({questions.length}/{MAX_QUESTIONS})
            </p>
            <button
              type="button"
              onClick={addQuestion}
              disabled={questions.length >= MAX_QUESTIONS}
              className="text-xs underline disabled:opacity-40"
            >
              Añadir pregunta
            </button>
          </div>
          {questions.map((item, index) => (
            <div key={index} className="space-y-2 border border-[#1E3A5F]/10 p-3">
              <div className="flex items-start justify-between gap-2">
                <label className="block flex-1 text-xs">
                  Pregunta {index + 1}
                  <input
                    value={item.question}
                    onChange={(event) => updateQuestionText(index, event.target.value)}
                    className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
                  />
                </label>
                {questions.length > 1 ? (
                  <button type="button" onClick={() => removeQuestion(index)} className="mt-5 text-xs text-[#C41E3A]">
                    Quitar
                  </button>
                ) : null}
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                {Array.from({ length: OPTIONS_PER_QUESTION }).map((_, optionIndex) => (
                  <label key={optionIndex} className="block text-xs">
                    <span className="flex items-center gap-1.5">
                      <input
                        type="radio"
                        name={`edit-correct-${assignment._id}-${index}`}
                        checked={item.correctIndex === optionIndex}
                        onChange={() => updateCorrectIndex(index, optionIndex)}
                      />
                      Opción {optionIndex + 1}
                    </span>
                    <input
                      value={item.options[optionIndex]}
                      onChange={(event) => updateOptionText(index, optionIndex, event.target.value)}
                      className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}
          {error ? <p className="text-xs text-[#C41E3A]">{error}</p> : null}
          {message ? <p className="text-xs text-[#1E3A5F]/65">{message}</p> : null}
          <button type="submit" disabled={busy} className="bg-[#1E3A5F] px-3 py-2 text-sm text-[#F4EFE6] disabled:opacity-50">
            {busy ? 'Guardando…' : 'Guardar preguntas'}
          </button>
        </form>
      )}
    </div>
  )
}

export default QuizEditor
